/* Copyright Information: Created by BuyGroup */
import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  FormGroup,
  Input,
  Label,
  Spinner,
} from "reactstrap";
import Moment from "react-moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getSupplierQuote,
  sendToCustomer,
  updateSupplierQuote,
} from "../services/QuoteServices";
import "../Quotes/Quotes.css";
import { formatAddress, formatDate } from "../utils";
import Timeline from "./Timeline";

const SupplierQuoteModal = ({
  isOpen,
  toggle,
  viewingSupplier,
  quoteData,
  quoteItems,
  isEditMode,
  quoteId,
  onSaveSuccess,
}) => {
  const [validUntil, setValidUntil] = useState(null);
  const [quoteExpiryDate, setQuoteExpiryDate] = useState(null);
  const [isPricingExpanded, setIsPricingExpanded] = useState(false);
  const [unitPrices, setUnitPrices] = useState({});
  const [tax, setTax] = useState("");
  const [shipping, setShipping] = useState("");
  const [buyGroupMargin, setBuyGroupMargin] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSendingToCustomer, setIsSendingToCustomer] = useState(false);
  const [timeline, setTimeline] = useState([]);

  useEffect(() => {
    if (quoteId && viewingSupplier?.id) {
      setLoading(true);
      getSupplierQuote(quoteId, viewingSupplier.id)
        .then((res) => {
          console.log("supplier quote details res", res.data);
          setTax(res.data.taxAndShipping.tax);
          setShipping(res.data.taxAndShipping.shipping);
          setTimeline(res.data.timeline);
          // Initialize unit prices from quote items
          const initialPrices = {};
          res.data.supplierEstimates.forEach((item) => {
            initialPrices[item.quote_item_id] = item.unit_price || "";
          });
          setUnitPrices(initialPrices);
        })
        .catch((err) => {
          console.error("error in get supplier quote details", err);
          toast.error("Failed to get supplier quote details");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [quoteId, viewingSupplier?.id]);

  // Initialize values when modal opens or supplier changes
  useEffect(() => {
    if (quoteData) {
      setValidUntil(
        quoteData.valid_date ? new Date(quoteData.valid_date) : null
      );
      setQuoteExpiryDate(
        quoteData.expiry_date ? new Date(quoteData.expiry_date) : null
      );

      setBuyGroupMargin(quoteData.margin_percentage || "");
      // Auto-expand pricing section in view mode
      if (!isEditMode) {
        setIsPricingExpanded(true);
      }
    }
  }, [isEditMode, quoteData]);

  // Validation function for positive values with max 2 decimal places
  const validatePriceInput = (value) => {
    // Allow empty string for clearing the field
    if (value === "") {
      return true;
    }

    // Check if value is negative
    if (parseFloat(value) < 0) {
      return false;
    }

    // Check decimal places (max 2 decimal places)
    const decimalParts = value.split(".");
    if (decimalParts.length > 1 && decimalParts[1].length > 2) {
      return false;
    }

    return true;
  };

  // Handle unit price change with validation
  const handleUnitPriceChange = (itemId, value) => {
    if (!validatePriceInput(value)) {
      return;
    }

    setUnitPrices((prev) => ({
      ...prev,
      [itemId]: value,
    }));
  };

  // Handle tax change with validation
  const handleTaxChange = (value) => {
    if (!validatePriceInput(value)) {
      return;
    }
    setTax(value);
  };

  // Handle shipping change with validation
  const handleShippingChange = (value) => {
    if (!validatePriceInput(value)) {
      return;
    }
    setShipping(value);
  };

  // Calculate subtotal for a single item
  const calculateSubtotal = (item) => {
    const unitPrice = parseFloat(unitPrices[item.id] || item.unit_price || 0);
    const quantity = parseInt(item.quantity || 0);
    return unitPrice * quantity;
  };

  // Calculate total subtotal from all items
  const calculateTotalSubtotal = () => {
    if (!quoteItems || quoteItems.length === 0) return 0;
    return quoteItems.reduce((total, item) => {
      return total + calculateSubtotal(item);
    }, 0);
  };

  // Calculate total (subtotal + tax + shipping)
  const calculateTotal = () => {
    const subtotal = calculateTotalSubtotal();
    const taxAmount = parseFloat(tax || 0);
    const shippingAmount = parseFloat(shipping || 0);
    return subtotal + taxAmount + shippingAmount;
  };

  // Handle save button click
  const handleSave = () => {
    if (!quoteId || !viewingSupplier?.id) {
      toast.error("Missing quote or supplier information");
      return;
    }

    setIsSaving(true);
    console.log("validUntil", validUntil, formatDate(validUntil));
    console.log(
      "quoteExpiryDate",
      quoteExpiryDate,
      formatDate(quoteExpiryDate)
    );

    // Prepare payload with updated data
    const payload = {
      validDate: validUntil ? formatDate(validUntil) : null,
      expiryDate: quoteExpiryDate ? formatDate(quoteExpiryDate) : null,
      tax: tax ? parseFloat(tax) : 0,
      shipping: shipping ? parseFloat(shipping) : 0,
      buyGroupMargin: buyGroupMargin || null,
      unitPrices: unitPrices,
      quoteId: quoteData.id,
    };

    updateSupplierQuote(quoteId, viewingSupplier.id, payload)
      .then((res) => {
        toast.success(res.message);
        if (onSaveSuccess) {
          onSaveSuccess();
        }
        toggle();
      })
      .catch((err) => {
        console.error("Error updating supplier quote:", err);
        toast.error("Failed to update supplier quote");
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  // Handle send to customer button click
  const handleSendToCustomer = () => {
    setIsSendingToCustomer(true);

    const payload = {
      quoteSupplierId: viewingSupplier.id,
      quoteId: quoteData.id,
    };

    sendToCustomer(quoteId, payload)
      .then((res) => {
        toast.success(res.message || "Quote sent to customer successfully");
        if (onSaveSuccess) {
          onSaveSuccess();
        }
        toggle();
      })
      .catch((err) => {
        console.error("Error sending to customer:", err);
        toast.error("Failed to send to customer");
      })
      .finally(() => {
        setIsSendingToCustomer(false);
      });
  };

  if (!viewingSupplier || !quoteData) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="xl"
      className="supplier-quote-modal"
    >
      <ModalHeader toggle={toggle}>
        {isEditMode ? "Add Supplier Quote" : "View Supplier Quote"}
      </ModalHeader>
      <ModalBody className="supplier-quote-body">
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "500px" }}
          >
            <Spinner animation="border" />
          </div>
        ) : (
          <div className="supplier-quote-content">
            {/* Quotation Header Section */}
            <div className="quotation-header-section">
              <div className="quotation-title-section">
                <div className="quotation-title-wrapper">
                  <h2 className="quotation-title">QUOTATION</h2>
                  <div className="supplier-info-section">
                    <h4>
                      <strong>{viewingSupplier.company_name}</strong>
                    </h4>
                    <div className="ship-to-info">
                      Ship To:&nbsp;
                      {quoteData?.address2 && quoteData.address2 + ", "}
                      {formatAddress(
                        quoteData.street,
                        quoteData.city,
                        quoteData.state,
                        quoteData.zip_code
                      )}
                    </div>
                  </div>
                </div>
                <div className="quotation-meta">
                  <div className="meta-item">
                    <span className="meta-label">Date:</span>
                    <span className="meta-value">
                      <Moment format="MM/DD/YYYY">{quoteData.date}</Moment>
                    </span>
                  </div>
                  <div className="meta-item">
                    <span className="meta-label">Valid Until:</span>
                    {isEditMode ? (
                      <div style={{ position: "relative", width: "75%" }}>
                        <DatePicker
                          selected={validUntil}
                          onChange={(date) => setValidUntil(date)}
                          dateFormat="MM/dd/yyyy"
                          className="form-control meta-input"
                          readOnly={!isEditMode}
                          disabled={!isEditMode}
                          placeholderText="MM/DD/YYYY"
                          wrapperClassName="w-100"
                        />
                        <i
                          className="fa fa-calendar"
                          style={{
                            position: "absolute",
                            right: "15px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: "#6c757d",
                            cursor: isEditMode ? "pointer" : "default",
                            pointerEvents: isEditMode ? "auto" : "none",
                            opacity: isEditMode ? 1 : 0.6,
                          }}
                        />
                      </div>
                    ) : (
                      <span className="meta-value">
                        {validUntil ? (
                          <Moment format="MM/DD/YYYY">{validUntil}</Moment>
                        ) : (
                          "N/A"
                        )}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Items Table */}
            {quoteItems && quoteItems.length > 0 && (
              <div className="quote-items-table-section">
                <table className="table quote-items-table">
                  <thead>
                    <tr>
                      <th>ITEM</th>
                      <th>ASIN</th>
                      <th>QUANTITY</th>
                      <th>UNIT PRICE</th>
                      <th>SUBTOTAL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quoteItems.map((item) => {
                      const unitPrice =
                        unitPrices[item.id] !== undefined
                          ? unitPrices[item.id]
                          : item.unit_price || "";
                      const subtotal = calculateSubtotal(item);
                      return (
                        <tr key={item.id}>
                          <td>{item.product_name}</td>
                          <td>{item.asin}</td>
                          <td>{item.quantity}</td>
                          <td>
                            {isEditMode ? (
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={unitPrice}
                                onChange={(e) =>
                                  handleUnitPriceChange(item.id, e.target.value)
                                }
                                className="unit-price-input"
                                placeholder="0.00"
                              />
                            ) : (
                              <span>
                                {unitPrice
                                  ? `$${parseFloat(unitPrice).toFixed(2)}`
                                  : "N/A"}
                              </span>
                            )}
                          </td>
                          <td>${subtotal.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Summary Section */}
            <div className="quote-summary-section">
              <div className="summary-row">
                <span className="summary-label">Subtotal :</span>
                <span className="summary-value">
                  ${calculateTotalSubtotal().toFixed(2)}
                </span>
              </div>
              <div className="summary-row">
                <span className="summary-label">Tax :</span>
                {isEditMode ? (
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={tax}
                    onChange={(e) => handleTaxChange(e.target.value)}
                    className="summary-input"
                    placeholder="0.00"
                  />
                ) : (
                  <span className="summary-value">
                    {tax ? `$${parseFloat(tax).toFixed(2)}` : "$0.00"}
                  </span>
                )}
              </div>
              <div className="summary-row">
                <span className="summary-label">Shipping :</span>
                {isEditMode ? (
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={shipping}
                    onChange={(e) => handleShippingChange(e.target.value)}
                    className="summary-input"
                    placeholder="0.00"
                  />
                ) : (
                  <span className="summary-value">
                    {shipping ? `$${parseFloat(shipping).toFixed(2)}` : "$0.00"}
                  </span>
                )}
              </div>
              <div className="summary-row total-row">
                <span className="summary-label">TOTAL :</span>
                <span className="summary-value total-value">
                  ${calculateTotal().toFixed(2)}
                </span>
              </div>
            </div>

            {/* BuyGroup Pricing and Margin Section */}
            <div className="buygroup-pricing-section">
              <Button
                color="secondary"
                className="add-pricing-btn"
                block
                onClick={() => setIsPricingExpanded(!isPricingExpanded)}
              >
                Add BuyGroup Pricing and Margin
                <i
                  className={`fa fa-chevron-${
                    isPricingExpanded ? "up" : "down"
                  } ml-2`}
                ></i>
              </Button>
              {isPricingExpanded && (
                <div className="pricing-inputs-section">
                  <FormGroup className="mt-3">
                    <Label>BuyGroup Margins</Label>
                    <Input
                      type="text"
                      value={buyGroupMargin}
                      onChange={(e) => setBuyGroupMargin(e.target.value)}
                      placeholder="Enter margin percentage or amount"
                      readOnly={!isEditMode}
                      disabled={!isEditMode}
                    />
                  </FormGroup>
                  <FormGroup className="mt-3">
                    <Label className="mb-2">Quote expiry date</Label>
                    <div style={{ position: "relative", width: "100%" }}>
                      <DatePicker
                        selected={quoteExpiryDate}
                        onChange={(date) => setQuoteExpiryDate(date)}
                        dateFormat="MM/dd/yyyy"
                        className="form-control w-100"
                        readOnly={!isEditMode}
                        disabled={!isEditMode}
                        placeholderText="Select expiry date"
                        wrapperClassName="w-100"
                      />
                      <i
                        className="fa fa-calendar"
                        style={{
                          position: "absolute",
                          right: "15px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#6c757d",
                          cursor: isEditMode ? "pointer" : "default",
                          pointerEvents: isEditMode ? "auto" : "none",
                          opacity: isEditMode ? 1 : 0.6,
                        }}
                      />
                    </div>
                  </FormGroup>
                </div>
              )}
            </div>

            {/* Timeline Section */}
            {timeline.length > 0 && <Timeline timeline={timeline} />}

            {/* Footer */}
            <div className="supplier-quote-footer">
              {isEditMode ? (
                <Button
                  color="primary"
                  className="send-to-customer-btn"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Save"}
                </Button>
              ) : quoteData.status === "sent_to_supplier" ? (
                <Button
                  color="primary"
                  className="send-to-customer-btn"
                  onClick={handleSendToCustomer}
                  disabled={isSendingToCustomer}
                >
                  {isSendingToCustomer ? "Sending..." : "Send to Customer"}
                </Button>
              ) : null}
            </div>
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

export default SupplierQuoteModal;
/* Copyright Information: Created by BuyGroup */
